import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, TextField, Button, IconButton, Stack, Snackbar, Alert,
  Autocomplete, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Divider, useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import api from "../services/api";

// Browser SpeechRecognition is vendor-prefixed in older Chromes / iOS Safari.
const SpeechRecognition = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

const TEXTS = {
  en: {
    newIntake: "New Intake",
    customer: "Customer (existing or walk-in)",
    date: "Date",
    material: "Material",
    materialPh: "e.g. Bare Bright Copper",
    gross: "Gross (lb)",
    tare: "Tare (lb)",
    net: "Net (lb)",
    addLine: "Add line",
    notes: "Notes (optional)",
    clear: "Clear",
    save: "Save Intake",
    saving: "Saving…",
    totalNet: "Total net:",
    drafts: (n) => `Your DRAFT intakes (${n})`,
    intakeCol: "Intake #",
    customerCol: "Customer",
    dateCol: "Date",
    linesCol: "Lines",
    totalNetCol: "Total Net (lb)",
    statusCol: "Status",
    noDrafts: "No drafts yet.",
    customerRequired: "Customer name required",
    addLineWarning: "Add at least one line with material + weight",
    saved: (num) => `Intake ${num} saved`,
    saveFailed: "Save failed",
    draftBadge: "DRAFT",
    takePhoto: "Take photo",
    voiceUnsupported: "Voice input not supported by this browser",
    listening: "Listening…",
    photoFailed: "Photo upload failed",
  },
  es: {
    newIntake: "Nueva Recepción",
    customer: "Cliente (existente o nuevo)",
    date: "Fecha",
    material: "Material",
    materialPh: "ej. Cobre Bare Bright",
    gross: "Bruto (lb)",
    tare: "Tara (lb)",
    net: "Neto (lb)",
    addLine: "Agregar línea",
    notes: "Notas (opcional)",
    clear: "Limpiar",
    save: "Guardar Recepción",
    saving: "Guardando…",
    totalNet: "Total neto:",
    drafts: (n) => `Tus recepciones BORRADOR (${n})`,
    intakeCol: "Recepción #",
    customerCol: "Cliente",
    dateCol: "Fecha",
    linesCol: "Líneas",
    totalNetCol: "Total Neto (lb)",
    statusCol: "Estado",
    noDrafts: "Sin borradores todavía.",
    customerRequired: "El nombre del cliente es obligatorio",
    addLineWarning: "Agrega al menos una línea con material y peso",
    saved: (num) => `Recepción ${num} guardada`,
    saveFailed: "Error al guardar",
    draftBadge: "BORRADOR",
    takePhoto: "Tomar foto",
    voiceUnsupported: "Este navegador no soporta entrada por voz",
    listening: "Escuchando…",
    photoFailed: "Error al subir foto",
  },
};

function todayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const emptyLine = () => ({
  uid: Math.random().toString(36).slice(2),
  Item_ID: null,
  Item_Description: "",
  G_Wt: "",
  T_Wt: "",
  PhotoFile: null,
});

function netOf(line) {
  const g = Number(line.G_Wt);
  const t = Number(line.T_Wt);
  if (Number.isFinite(g) && Number.isFinite(t)) return Math.max(0, +(g - t).toFixed(2));
  return null;
}

export default function IntakeForm({ onSaved, lang = "es" }) {
  const t = TEXTS[lang === "en" ? "en" : "es"];
  const [accounts, setAccounts]   = useState([]);
  const [products, setProducts]   = useState([]);
  const [customer, setCustomer]   = useState(null);     // {id, SubAccount_Name} or null
  const [customerText, setCustomerText] = useState(""); // typed-in name for walk-ins
  const [intakeDate, setIntakeDate] = useState(todayStr());
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [toast, setToast]   = useState({ open: false, message: "", severity: "info" });
  const [listeningUid, setListeningUid] = useState(null);   // which line's mic is active
  const [uploadingUid, setUploadingUid] = useState(null);   // which line's photo is uploading

  // Switch to a stacked card layout on phones so the form fits without
  // horizontal scrolling. Anything wider than the sm breakpoint keeps the
  // existing table layout.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const showToast = (message, severity = "info") => setToast({ open: true, message, severity });
  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  // Load accounts + products + recent intakes
  const loadRecent = useCallback(async () => {
    try {
      const r = await api.get("/intakes", { params: { status: "DRAFT", mine: true } });
      setRecent(r.data?.intakes || []);
    } catch (_) { /* not fatal */ }
  }, []);

  useEffect(() => {
    let alive = true;
    // These ungated lookup endpoints work for every role (warehouse users
    // who only have the EMPLOYEE PANEL module + role=cashier still get
    // them). The mirror endpoints under /purchaseInvoices are gated.
    api.get("/containerimages/accounts").then((r) => alive && setAccounts(r.data || [])).catch(() => {});
    api.get("/containerimages/products").then((r) => alive && setProducts(r.data || [])).catch(() => {});
    loadRecent();
    return () => { alive = false; };
  }, [loadRecent]);

  // Customer name is whatever they typed if no account is selected
  const finalCustomerName = useMemo(() => {
    if (customer?.SubAccount_Name) return customer.SubAccount_Name;
    return customerText.trim();
  }, [customer, customerText]);

  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (uid) => setLines((ls) => (ls.length > 1 ? ls.filter((l) => l.uid !== uid) : ls));
  const updateLine = (uid, patch) => setLines((ls) => ls.map((l) => (l.uid === uid ? { ...l, ...patch } : l)));

  // ----- Photo capture per line -----
  const handlePhotoChange = async (uid, file) => {
    if (!file) return;
    setUploadingUid(uid);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      const r = await api.post("/intakes/upload-photo", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateLine(uid, { PhotoFile: r.data.filename });
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showToast(`${t.photoFailed}: ${msg}`, "error");
    } finally {
      setUploadingUid(null);
    }
  };

  // ----- Voice input for material name (scaffolding; mic button not yet wired) -----
  // eslint-disable-next-line no-unused-vars
  const startListening = (uid) => {
    if (!SpeechRecognition) {
      showToast(t.voiceUnsupported, "warning");
      return;
    }
    const rec = new SpeechRecognition();
    rec.lang = lang === "es" ? "es-ES" : "en-US";
    rec.interimResults = false;
    rec.maxAlternatives = 1;
    rec.continuous = false;
    setListeningUid(uid);
    rec.onresult = (e) => {
      const transcript = e.results?.[0]?.[0]?.transcript || "";
      if (transcript) updateLine(uid, { Item_Description: transcript, Item_ID: null });
    };
    rec.onerror = () => setListeningUid(null);
    rec.onend = () => setListeningUid(null);
    try { rec.start(); } catch (_) { setListeningUid(null); }
  };

  const totalNet = useMemo(
    () => lines.reduce((sum, l) => sum + (netOf(l) || 0), 0),
    [lines],
  );

  const handleSave = async () => {
    if (!finalCustomerName) return showToast(t.customerRequired, "warning");
    const validLines = lines.filter((l) => l.Item_Description.trim() && (l.G_Wt !== "" || l.T_Wt !== ""));
    if (!validLines.length) return showToast(t.addLineWarning, "warning");

    setSaving(true);
    try {
      const payload = {
        Customer_ID:   customer?.id ?? null,
        Customer_Name: finalCustomerName,
        IntakeDate:    intakeDate,
        Notes:         notes || null,
        Lines: validLines.map((l, i) => ({
          Item_ID:          l.Item_ID,
          Item_Description: l.Item_Description.trim(),
          G_Wt:             l.G_Wt === "" ? null : Number(l.G_Wt),
          T_Wt:             l.T_Wt === "" ? null : Number(l.T_Wt),
          N_Wt:             netOf(l),
          PhotoFile:        l.PhotoFile || null,
          SortOrder:        i,
        })),
      };
      const r = await api.post("/intakes", payload);
      showToast(t.saved(r.data?.IntakeNumber), "success");
      // Reset form
      setCustomer(null);
      setCustomerText("");
      setNotes("");
      setLines([emptyLine()]);
      setIntakeDate(todayStr());
      loadRecent();
      onSaved && onSaved(r.data);
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      showToast(`${t.saveFailed}: ${msg}`, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t.newIntake}</Typography>

      <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
        {/* Customer + date */}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <Autocomplete
            freeSolo
            options={accounts}
            getOptionLabel={(o) => (typeof o === "string" ? o : o?.SubAccount_Name || "")}
            value={customer || customerText}
            onChange={(_, v) => {
              if (typeof v === "string") { setCustomer(null); setCustomerText(v); }
              else { setCustomer(v); setCustomerText(v?.SubAccount_Name || ""); }
            }}
            onInputChange={(_, v, reason) => {
              if (reason === "input") setCustomerText(v);
            }}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            sx={{ flex: 1 }}
            renderInput={(p) => (
              <TextField {...p} label={t.customer} size="small" required />
            )}
          />
          <TextField
            label={t.date}
            type="date"
            value={intakeDate}
            onChange={(e) => setIntakeDate(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ width: { xs: "100%", md: 180 } }}
          />
        </Stack>

        {/* ===== Lines: card stack on mobile, table on desktop ===== */}
        {isMobile ? (
          <Box sx={{ mt: 2 }}>
            {lines.map((line, idx) => {
              // eslint-disable-next-line no-unused-vars
              const isListening = listeningUid === line.uid;
              const isUploading = uploadingUid === line.uid;
              return (
                <Paper key={line.uid} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>
                      #{idx + 1}
                    </Typography>
                    <IconButton size="small" onClick={() => removeLine(line.uid)} disabled={lines.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Material full width */}
                  <Box sx={{ mb: 1 }}>
                    <Autocomplete
                      freeSolo
                      options={products}
                      getOptionLabel={(o) => (typeof o === "string" ? o : o?.Product_Description || "")}
                      value={
                        line.Item_ID
                          ? products.find((p) => p.Item_ID === line.Item_ID) || line.Item_Description
                          : line.Item_Description
                      }
                      onChange={(_, v) => {
                        if (typeof v === "string") {
                          updateLine(line.uid, { Item_ID: null, Item_Description: v });
                        } else {
                          updateLine(line.uid, {
                            Item_ID: v?.Item_ID ?? null,
                            Item_Description: v?.Product_Description || "",
                          });
                        }
                      }}
                      onInputChange={(_, v, reason) => {
                        if (reason === "input") updateLine(line.uid, { Item_Description: v, Item_ID: null });
                      }}
                      renderInput={(p) => <TextField {...p} label={t.material} placeholder={t.materialPh} size="small" />}
                      isOptionEqualToValue={(opt, val) => opt?.Item_ID === val?.Item_ID}
                      fullWidth
                    />
                  </Box>

                  {/* Photo: two buttons — Camera (live) + Gallery (existing) */}
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Button
                      size="small"
                      component="label"
                      variant="outlined"
                      startIcon={<PhotoCameraIcon />}
                      color={line.PhotoFile ? "success" : "primary"}
                      disabled={isUploading}
                      fullWidth
                    >
                      Camera
                      <input hidden type="file" accept="image/*" capture="environment"
                        onChange={(e) => handlePhotoChange(line.uid, e.target.files?.[0])} />
                    </Button>
                    <Button
                      size="small"
                      component="label"
                      variant="outlined"
                      startIcon={<PhotoLibraryIcon />}
                      color={line.PhotoFile ? "success" : "primary"}
                      disabled={isUploading}
                      fullWidth
                    >
                      Gallery
                      <input hidden type="file" accept="image/*"
                        onChange={(e) => handlePhotoChange(line.uid, e.target.files?.[0])} />
                    </Button>
                  </Stack>

                  {/* Gross + Tare on one row */}
                  <Stack direction="row" spacing={1}>
                    <TextField
                      label={t.gross}
                      value={line.G_Wt}
                      onChange={(e) => updateLine(line.uid, { G_Wt: e.target.value })}
                      size="small"
                      type="number"
                      fullWidth
                      inputProps={{ inputMode: "decimal", step: 0.01, min: 0, style: { textAlign: "right" } }}
                    />
                    <TextField
                      label={t.tare}
                      value={line.T_Wt}
                      onChange={(e) => updateLine(line.uid, { T_Wt: e.target.value })}
                      size="small"
                      type="number"
                      fullWidth
                      inputProps={{ inputMode: "decimal", step: 0.01, min: 0, style: { textAlign: "right" } }}
                    />
                  </Stack>

                  {/* Net + photo preview */}
                  <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>{t.net}</Typography>
                      <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 18 }}>
                        {netOf(line) ?? "—"}
                      </Typography>
                    </Box>
                    {line.PhotoFile && (
                      <Box
                        component="img"
                        src={`/uploads/${line.PhotoFile}`}
                        alt=""
                        onClick={() => window.open(`/uploads/${line.PhotoFile}`, "_blank")}
                        sx={{ width: 56, height: 56, borderRadius: 1, objectFit: "cover", cursor: "pointer", border: "1px solid #ddd" }}
                      />
                    )}
                  </Box>
                </Paper>
              );
            })}

            <Paper variant="outlined" sx={{ p: 1.5, mb: 1, bgcolor: "action.hover" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontWeight: 700 }}>{t.totalNet}</Typography>
                <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: 20 }}>
                  {totalNet.toFixed(2)}
                </Typography>
              </Box>
            </Paper>
          </Box>
        ) : (
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", minWidth: 220 }}>{t.material}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", width: 110 }}>{t.gross}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", width: 110 }}>{t.tare}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold", width: 110 }}>{t.net}</TableCell>
                  <TableCell width={48} />
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line) => {
                  // eslint-disable-next-line no-unused-vars
                  const isListening = listeningUid === line.uid;
                  const isUploading = uploadingUid === line.uid;
                  return (
                  <TableRow key={line.uid}>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Autocomplete
                          freeSolo
                          options={products}
                          getOptionLabel={(o) => (typeof o === "string" ? o : o?.Product_Description || "")}
                          value={
                            line.Item_ID
                              ? products.find((p) => p.Item_ID === line.Item_ID) || line.Item_Description
                              : line.Item_Description
                          }
                          onChange={(_, v) => {
                            if (typeof v === "string") {
                              updateLine(line.uid, { Item_ID: null, Item_Description: v });
                            } else {
                              updateLine(line.uid, {
                                Item_ID: v?.Item_ID ?? null,
                                Item_Description: v?.Product_Description || "",
                              });
                            }
                          }}
                          onInputChange={(_, v, reason) => {
                            if (reason === "input") updateLine(line.uid, { Item_Description: v, Item_ID: null });
                          }}
                          renderInput={(p) => <TextField {...p} placeholder={t.materialPh} size="small" />}
                          isOptionEqualToValue={(opt, val) => opt?.Item_ID === val?.Item_ID}
                          sx={{ flex: 1 }}
                        />
                        <IconButton
                          size="small"
                          component="label"
                          title="Camera"
                          color={line.PhotoFile ? "success" : "default"}
                          disabled={isUploading}
                        >
                          <PhotoCameraIcon fontSize="small" />
                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => handlePhotoChange(line.uid, e.target.files?.[0])}
                          />
                        </IconButton>
                        <IconButton
                          size="small"
                          component="label"
                          title="Gallery"
                          color={line.PhotoFile ? "success" : "default"}
                          disabled={isUploading}
                        >
                          <PhotoLibraryIcon fontSize="small" />
                          <input
                            hidden
                            type="file"
                            accept="image/*"
                            onChange={(e) => handlePhotoChange(line.uid, e.target.files?.[0])}
                          />
                        </IconButton>
                        {line.PhotoFile && (
                          <Box
                            component="img"
                            src={`/uploads/${line.PhotoFile}`}
                            alt=""
                            onClick={() => window.open(`/uploads/${line.PhotoFile}`, "_blank")}
                            sx={{ width: 40, height: 40, borderRadius: 1, objectFit: "cover", cursor: "pointer", border: "1px solid #ddd" }}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        value={line.G_Wt}
                        onChange={(e) => updateLine(line.uid, { G_Wt: e.target.value })}
                        size="small"
                        type="number"
                        inputProps={{ inputMode: "decimal", step: 0.01, min: 0, style: { textAlign: "right" } }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        value={line.T_Wt}
                        onChange={(e) => updateLine(line.uid, { T_Wt: e.target.value })}
                        size="small"
                        type="number"
                        inputProps={{ inputMode: "decimal", step: 0.01, min: 0, style: { textAlign: "right" } }}
                        sx={{ width: 100 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                      {netOf(line) ?? "—"}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => removeLine(line.uid)} disabled={lines.length === 1}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={3} align="right" sx={{ fontWeight: "bold" }}>{t.totalNet}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                    {totalNet.toFixed(2)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ mt: 1 }}>
          <Button
            variant={isMobile ? "outlined" : "text"}
            fullWidth={isMobile}
            size={isMobile ? "medium" : "small"}
            startIcon={<AddIcon />}
            onClick={addLine}
          >
            {t.addLine}
          </Button>
        </Box>

        <TextField
          label={t.notes}
          multiline minRows={2} maxRows={4} fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          size="small"
          sx={{ mt: 2 }}
        />

        <Stack
          direction={{ xs: "column-reverse", sm: "row" }}
          spacing={1}
          sx={{ mt: 2 }}
          justifyContent={{ sm: "flex-end" }}
        >
          <Button
            fullWidth={isMobile}
            onClick={() => { setCustomer(null); setCustomerText(""); setNotes(""); setLines([emptyLine()]); }}
            disabled={saving}
          >
            {t.clear}
          </Button>
          <Button
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? "large" : "medium"}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? t.saving : t.save}
          </Button>
        </Stack>
      </Paper>

      {/* Recent drafts */}
      <Divider sx={{ my: 2 }}><Chip label={t.drafts(recent.length)} size="small" /></Divider>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>{t.intakeCol}</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>{t.customerCol}</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>{t.dateCol}</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>{t.linesCol}</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold" }}>{t.totalNetCol}</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>{t.statusCol}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {recent.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 3 }}>{t.noDrafts}</TableCell></TableRow>
            )}
            {recent.map((r) => {
              const statusLabel = r.Status === "DRAFT" ? t.draftBadge : r.Status;
              return (
                <TableRow key={r.Intake_ID} hover>
                  <TableCell sx={{ fontFamily: "monospace" }}>{r.IntakeNumber}</TableCell>
                  <TableCell>{r.Customer_Name}</TableCell>
                  <TableCell>{r.IntakeDate?.slice(0, 10)}</TableCell>
                  <TableCell align="right">{r.LineCount}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{r.TotalNetWt != null ? Number(r.TotalNetWt).toFixed(2) : "—"}</TableCell>
                  <TableCell><Chip size="small" label={statusLabel} color={r.Status === "DRAFT" ? "warning" : "default"} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={toast.open} autoHideDuration={4000} onClose={closeToast} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={toast.severity} onClose={closeToast} variant="filled">{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
